import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { inject, observer } from 'mobx-react';
import {
    Button,
    Paper,
    Stack,
    Title,
    NumberInput,
    Select,
    Group,
    Text,
    Alert,
    Badge,
    ActionIcon,
    Box,
    ScrollArea,
    Table,
} from '@mantine/core';
import { IconPlus, IconTrash, IconAlertTriangle, IconDeviceFloppy, IconAlertCircle } from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetMMEDrugRouteCombinations } from '../../../global/entities/MME/queries/useGetMMEDrugRouteCombinations.tsx';
import { useGetFentanylDoseOptions } from '../../../global/entities/MME/queries/useGetFentanylDoseOptions.tsx';
import { useCalculateMME } from '../../../global/entities/MME/mutations/useCalculateMME.tsx';
import { useSaveMMECalculation } from '../../../global/entities/MME/mutations/useSaveMMECalculation.tsx';
import { IOpioidDoseInput, IMMECalculationResult, MMERiskLevel, SpecialOpioidFormulaType, DrugRoute } from '../../../global/entities/MME/MME.ts';
import { PatientStore } from '../../../global/Store/PatientStore.ts';
import { notifications } from '@mantine/notifications';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';

type MMECalculatorLocationState = {
    mmeReopen?: {
        savedCalculationId: number;
        inputData: IOpioidDoseInput[];
        canBeDeleted?: boolean;
        conversionDataVersionID?: number;
    };
};

const MMECalculatorTable: React.FC = inject('patientStore')(
    observer(({ patientStore }: { patientStore?: PatientStore }) => {
        const { t } = useTranslation();
        const navigate = useNavigate();
        const location = useLocation();
        const { sortedData: opioidDoses, setSortedData: setOpioidDoses, sortBy, reverseSortDirection, setSorting } = useTable();
        const [calculationResult, setCalculationResult] = useState<IMMECalculationResult | null>(null);
        const isInitialMount = useRef(true);

        // Get conversion version ID from location state (for saved calculations)
        const locationState = (location.state || {}) as MMECalculatorLocationState;
        const conversionDataVersionID = locationState.mmeReopen?.conversionDataVersionID;

        const { data: drugRouteCombinations, isLoading: combinationsLoading } = useGetMMEDrugRouteCombinations(conversionDataVersionID);
        const { data: fentanylConfig } = useGetFentanylDoseOptions(conversionDataVersionID);
        const { mutate: calculateMME } = useCalculateMME();
        const saveCalculation = useSaveMMECalculation();

        // Determine if this is a read-only view (calculation that cannot be deleted)
        const isReadOnly = locationState.mmeReopen?.canBeDeleted === false;

        // If we landed here from a saved calculation row action, hydrate inputs and let the existing auto-calc run
        useEffect(() => {
            const state = (location.state || {}) as MMECalculatorLocationState;
            const inputData = state.mmeReopen?.inputData;
            if (!inputData || inputData.length === 0) return;

            setOpioidDoses(
                inputData.map((dose) => ({
                    drugID: dose.drugID ?? 0,
                    routeID: dose.routeID ?? 0,
                    dose: dose.dose ?? 0,
                    unitID: dose.unitID ?? 1,
                    frequencyPerDay: dose.frequencyPerDay ?? 1,
                })),
            );
        }, [location.state, setOpioidDoses]);

        const drugRouteCombobox = useMemo(() => {
            if (!drugRouteCombinations) return [];
            const language = localStorage.getItem('i18nextLng') || 'fr';
            const combinations: { value: string; label: string; drugID: number; routeID: number; unitID: number; unitName: string; specialOpioidFormulaType: number }[] = [];
            
            drugRouteCombinations.forEach((combination) => {
                const drugName = language === 'fr' ? combination.drugNameFr : combination.drugNameEn;
                const routeName = language === 'fr' ? combination.routeNameFr : combination.routeNameEn;
                combinations.push({
                    value: `${combination.drugID}:${combination.routeID}`,
                    label: `${drugName} (${routeName})`,
                    drugID: combination.drugID,
                    routeID: combination.routeID,
                    unitID: combination.unitID,
                    unitName: combination.unitName,
                    specialOpioidFormulaType: combination.specialOpioidFormulaType,
                });
            });
            
            return combinations.sort((a, b) => a.label.localeCompare(b.label));
        }, [drugRouteCombinations]);

        // Fentanyl dose preset options for Select
        const fentanylDoseOptions = useMemo(() => {
            if (!fentanylConfig?.doseEquivalents || fentanylConfig.doseEquivalents.length === 0) return [];
            return fentanylConfig.doseEquivalents.map((eq) => ({
                value: eq.doseMcgPerHour.toString()
            }));
        }, [fentanylConfig]);

        // Helper function to check if a drug/route is TransdermalFentanyl
        const isTransdermalFentanyl = (drugID: number, routeID: number): boolean => {
            const combination = drugRouteCombobox.find(
                (c) => c.drugID === drugID && c.routeID === routeID
            );
            return combination?.specialOpioidFormulaType === SpecialOpioidFormulaType.TransdermalFentanyl;
        };

        // Helper function to check if a drug/route is Transdermal Buprenorphine
        const isTransdermalBuprenorphine = (drugID: number, routeID: number): boolean => {
            const combination = drugRouteCombobox.find(
                (c) => c.drugID === drugID && c.routeID === routeID && c.routeID === DrugRoute.Transdermal
            );
            return combination?.specialOpioidFormulaType === SpecialOpioidFormulaType.Buprenorphine;
        };

        // Helper function to check if we should show a patch assumption label
        const shouldShowPatchLabel = (drugID: number, routeID: number): boolean => {
            return isTransdermalFentanyl(drugID, routeID) || isTransdermalBuprenorphine(drugID, routeID);
        };

        // Helper function to get unit for a specific drug/route combination
        const getUnitForCombination = (drugID: number, routeID: number): string => {
            const combination = drugRouteCombobox.find(
                (c) => c.drugID === drugID && c.routeID === routeID
            );
            return combination?.unitName || 'mg';
        };

        const performCalculation = useCallback((doses: IOpioidDoseInput[]) => {
            const validDoses = doses.filter(
                (dose) => dose.drugID > 0 && dose.routeID > 0 && dose.dose > 0,
            );

            if (validDoses.length === 0) {
                setCalculationResult(null);
                return;
            }

            calculateMME(
                { opioidDoses: validDoses },
                {
                    onSuccess: (result) => {
                        setCalculationResult(result);
                    },
                    onError: (error) => {
                        console.error('Erreur lors du calcul MME:', error);
                        setCalculationResult(null);
                    },
                },
            );
        }, [calculateMME]);

        const addOpioidDose = () => {
            setOpioidDoses((prev) => [
                ...prev,
                {
                    drugID: 0,
                    routeID: 0,
                    dose: 0,
                    unitID: 1, // Default value for backend
                    frequencyPerDay: 1,
                },
            ]);
        };

        const removeOpioidDose = (index: number) => {
            setOpioidDoses((prev) => prev.filter((_, i) => i !== index));
        };

        const updateOpioidDose = (index: number, field: keyof IOpioidDoseInput, value: any) => {
            setOpioidDoses((prev) =>
                prev.map((dose, i) => {
                    if (i === index) 
                        return { ...dose, [field]: value };
                    
                    return dose;
                }),
            );
        };

        const handleSave = async () => {
            if (!calculationResult || !patientStore?.patientId) {
                notifications.show({
                    title: t('mme.calculator.saveError', 'Erreur'),
                    message: t('mme.calculator.saveErrorPatientRequired', 'Un patient doit être sélectionné pour sauvegarder le calcul.'),
                    color: 'red',
                });
                return;
            }

            try {
                const savedCalculationId = (location.state as MMECalculatorLocationState | undefined)?.mmeReopen?.savedCalculationId;
                await saveCalculation.mutateAsync({
                    patientID: parseInt(patientStore.patientId),
                    savedCalculationId: savedCalculationId,
                    calculationRequest: {
                        opioidDoses: opioidDoses.filter(
                            (dose) => dose.drugID > 0 && dose.routeID > 0 && dose.dose > 0
                        ),
                    },
                    calculationResult: calculationResult,
                });

                navigate('/hcpPatientDashboard/dosageModeling');
            } catch {
                notifications.show({
                    title: t('mme.calculator.saveError', 'Erreur'),
                    message: t('mme.calculator.saveErrorMessage', 'Une erreur est survenue lors de la sauvegarde du calcul.'),
                    color: 'red',
                });
            }
        };

        // Auto-calculate as soon as there is at least one valid row
        useEffect(() => {
            if (isInitialMount.current) {
                isInitialMount.current = false;
                return;
            }
            performCalculation(opioidDoses);
        }, [opioidDoses, performCalculation]);

        return (
            <Paper p="md" withBorder style={{ display: 'flex', flexDirection: 'column' }}>
                <Stack gap="lg" style={{ flex: 1, overflow: 'auto' }}>
                    <Box>
                        <Group justify="space-between" align="center">
                            <Title order={4}>
                                {t('mme.calculator.title', 'MME Calculator')}
                            </Title>
                            {isReadOnly && (
                                <Badge color="gray" variant="light" size="lg">
                                    {t('mme.calculator.readOnly', 'Lecture seule')}
                                </Badge>
                            )}
                        </Group>
                        {isReadOnly && (
                            <Alert color="blue" mt="xs">
                                <Text size="sm">
                                    {t('mme.calculator.readOnlyMessage', 'Ce calcul est associé à un plan de tapering et ne peut pas être modifié.')}
                                </Text>
                            </Alert>
                        )}
                    </Box>
                    <Box>
                        <ScrollArea w="100%">
                            <Table>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Th
                                            sorted={sortBy === 'drugID'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('drugID', opioidDoses)}
                                        >
                                            {t('mme.calculator.drug', 'Drug')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'frequencyPerDay'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('frequencyPerDay', opioidDoses)}
                                        >
                                            {t('mme.calculator.frequency', 'Frequency/day')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'dose'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('dose', opioidDoses)}
                                        >
                                            {t('mme.calculator.dosage', 'Dosage')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'unitID'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('unitID', opioidDoses)}
                                        >
                                            {t('mme.calculator.dosePerDayUnit', 'Unit')}
                                        </Th>
                                        <Table.Th />
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {opioidDoses.length === 0 ? (
                                        <Table.Tr>
                                            <Table.Td colSpan={5}>
                                                <Text size="sm" c="dimmed">
                                                    {t('mme.calculator.description', 'Ajoutez les opioïdes et leurs doses pour calculer le total MME quotidien')}
                                                </Text>
                                            </Table.Td>
                                        </Table.Tr>
                                    ) : (
                                        opioidDoses.map((dose, index) => (
                                            <React.Fragment key={index}>
                                                <Table.Tr style={shouldShowPatchLabel(dose.drugID, dose.routeID) ? { borderBottom: 'none' } : undefined}>
                                                    <Table.Td style={{ minWidth: 280, borderBottom: shouldShowPatchLabel(dose.drugID, dose.routeID) ? 'none' : undefined }}>
                                                        <Stack gap={4}>
                                                            <Select
                                                                placeholder={
                                                                    combinationsLoading
                                                                        ? t('mme.calculator.loading', 'Chargement...')
                                                                        : drugRouteCombobox.length === 0
                                                                        ? t(
                                                                            'mme.calculator.noDrugs',
                                                                            'Aucun médicament disponible. Vérifiez que des facteurs de conversion sont configurés.',
                                                                        )
                                                                        : t('mme.calculator.chooseOne', 'Choose one')
                                                                }
                                                                data={drugRouteCombobox}
                                                                value={
                                                                    dose.drugID > 0 && dose.routeID > 0
                                                                        ? `${dose.drugID}:${dose.routeID}`
                                                                        : null
                                                                }
                                                                onChange={(value) => {
                                                                    if (value) {
                                                                        const [drugID, routeID] = value.split(':').map(Number);
                                                                        const combination = drugRouteCombobox.find(
                                                                            (c) => c.drugID === drugID && c.routeID === routeID,
                                                                        );
                                                                        updateOpioidDose(index, 'drugID', drugID);
                                                                        updateOpioidDose(index, 'routeID', routeID);
                                                                        if (combination) updateOpioidDose(index, 'unitID', combination.unitID);
                                                                        
                                                                        // Reset frequencyPerDay to 1 if not a patch drug
                                                                        if (!shouldShowPatchLabel(drugID, routeID)) updateOpioidDose(index, 'frequencyPerDay', 1);
                                                                    } else {
                                                                        updateOpioidDose(index, 'drugID', 0);
                                                                        updateOpioidDose(index, 'routeID', 0);
                                                                        updateOpioidDose(index, 'unitID', 1);
                                                                        updateOpioidDose(index, 'frequencyPerDay', 1);
                                                                    }
                                                                }}
                                                                searchable
                                                                disabled={isReadOnly || combinationsLoading || drugRouteCombobox.length === 0}
                                                                readOnly={isReadOnly}
                                                            />
                                                        </Stack>
                                                    </Table.Td>
                                                    <Table.Td style={{ width: 120, borderBottom: shouldShowPatchLabel(dose.drugID, dose.routeID) ? 'none' : undefined }}>
                                                        {shouldShowPatchLabel(dose.drugID, dose.routeID) ? (
                                                            <NumberInput
                                                                placeholder="N/A"
                                                                value={undefined}
                                                                disabled={true}
                                                                hideControls
                                                            />
                                                        ) : (
                                                            <NumberInput
                                                                placeholder="1"
                                                                value={dose.frequencyPerDay || 1}
                                                                defaultValue={1}
                                                                onChange={(value) => {
                                                                    const frequencyValue = typeof value === 'number' && value > 0 ? value : 1;
                                                                    updateOpioidDose(index, 'frequencyPerDay', frequencyValue);
                                                                }}
                                                                min={0.1}
                                                                max={24}
                                                                decimalScale={1}
                                                                allowDecimal
                                                                hideControls
                                                                disabled={isReadOnly}
                                                                readOnly={isReadOnly}
                                                            />
                                                        )}
                                                    </Table.Td>
                                                    <Table.Td style={{ width: 120, borderBottom: shouldShowPatchLabel(dose.drugID, dose.routeID) ? 'none' : undefined }}>
                                                        {isTransdermalFentanyl(dose.drugID, dose.routeID) && fentanylDoseOptions.length > 0 ? (
                                                            <Select
                                                                placeholder={t('mme.calculator.selectDose', 'Select dose')}
                                                                data={fentanylDoseOptions}
                                                                value={dose.dose > 0 ? dose.dose.toString() : null}
                                                                onChange={(value) => {
                                                                    const dosageValue = value ? parseFloat(value) : 0;
                                                                    updateOpioidDose(index, 'dose', dosageValue);
                                                                }}
                                                                disabled={isReadOnly}
                                                                readOnly={isReadOnly}
                                                                searchable
                                                                clearable
                                                            />
                                                        ) : (
                                                            <NumberInput
                                                                placeholder="0"
                                                                value={dose.dose > 0 ? dose.dose : undefined}
                                                                onChange={(value) => {
                                                                    const dosageValue = typeof value === 'number' ? value : 0;
                                                                    updateOpioidDose(index, 'dose', dosageValue);
                                                                }}
                                                                min={0}
                                                                decimalScale={2}
                                                                allowDecimal
                                                                hideControls
                                                                disabled={isReadOnly}
                                                                readOnly={isReadOnly}
                                                            />
                                                        )}
                                                    </Table.Td>
                                                    <Table.Td style={{ width: 80, borderBottom: shouldShowPatchLabel(dose.drugID, dose.routeID) ? 'none' : undefined }}>
                                                        <Text size="sm" fw={500}>
                                                            {dose.drugID > 0 && dose.routeID > 0
                                                                ? getUnitForCombination(dose.drugID, dose.routeID)
                                                                : t('mme.calculator.dosePerDayUnit', 'mg')}
                                                        </Text>
                                                    </Table.Td>
                                                    <Table.Td style={{ width: 60, borderBottom: shouldShowPatchLabel(dose.drugID, dose.routeID) ? 'none' : undefined }}>
                                                        {opioidDoses.length > 1 && !isReadOnly && (
                                                            <ActionIcon
                                                                color="red"
                                                                variant="subtle"
                                                                onClick={() => removeOpioidDose(index)}
                                                            >
                                                                <IconTrash size={16} />
                                                            </ActionIcon>
                                                        )}
                                                    </Table.Td>
                                                </Table.Tr>
                                                {shouldShowPatchLabel(dose.drugID, dose.routeID) && (
                                                    <Table.Tr>
                                                        <Table.Td colSpan={5} style={{ paddingTop: 0 }}>
                                                            <Text size="xs" c="dimmed" fs="italic">
                                                                {isTransdermalFentanyl(dose.drugID, dose.routeID)
                                                                    ? t('mme.calculator.fentanylPatchAssumption', 'Assumes 1 patch every 3 days')
                                                                    : t('mme.calculator.buprenorphinePatchAssumption', 'Assumes 1 patch every 7 days')}
                                                            </Text>
                                                        </Table.Td>
                                                    </Table.Tr>
                                                )}
                                            </React.Fragment>
                                        ))
                                    )}
                                </Table.Tbody>
                            </Table>
                        </ScrollArea>

                        <Stack mt="md" gap="sm" style={{ width: 'fit-content', marginLeft: 'auto' }}>
                            {!isReadOnly && (
                                <Button
                                    fullWidth
                                    leftSection={<IconPlus size={16} />}
                                    variant="filled"
                                    onClick={addOpioidDose}
                                >
                                    {t('mme.calculator.addOpioid', 'Add opioid')}
                                </Button>
                            )}

                            <Group align={'flex-end'} w="100%" gap="sm" wrap="nowrap">
                                <Button
                                    style={{ flex: 1 }}
                                    variant="outline"
                                    onClick={() => navigate('/hcpPatientDashboard/dosageModeling')}
                                >
                                    {isReadOnly ? t('button.close', 'Fermer') : t('button.cancel', 'Annuler')}
                                </Button>
                                {!isReadOnly && (
                                    <Button
                                        leftSection={<IconDeviceFloppy size={16} />}
                                        onClick={handleSave}
                                        loading={saveCalculation.isPending}
                                        disabled={!patientStore?.patientId}
                                        variant="filled"
                                    >
                                        {t('mme.calculator.save', 'Sauvegarder le calcul')}
                                    </Button>
                                )}
                            </Group>
                        </Stack>
                    </Box>

                    {calculationResult && (
                        <Box>
                            <Title order={4} mb="md">
                                {t('mme.calculator.result', 'Calculation Result')}
                            </Title>

                            <Paper p="md" withBorder>
                                <Group justify="space-between" mb="md">
                                    <Text size="lg" fw={700}>
                                        {t('mme.calculator.totalDailyMME', 'Total Daily MME')}:
                                    </Text>
                                    <Badge 
                                        size="xl" 
                                        color={
                                            calculationResult.riskLevel === MMERiskLevel.High 
                                                ? 'red' 
                                                : calculationResult.riskLevel === MMERiskLevel.Moderate 
                                                ? 'orange' 
                                                : 'blue'
                                        } 
                                        variant="light"
                                    >
                                        {calculationResult.totalDailyMME.toFixed(2)} {t('mme.calculator.dailyMME')}
                                    </Badge>
                                </Group>

                                {/* Risk alert display */}
                                {calculationResult.riskLevel !== MMERiskLevel.None && calculationResult.riskMessage && (
                                    <Alert
                                        icon={
                                            calculationResult.riskLevel === MMERiskLevel.High 
                                                ? <IconAlertCircle size={16} /> 
                                                : <IconAlertTriangle size={16} />
                                        }
                                        title={calculationResult.riskLevelDisplay || 
                                            (calculationResult.riskLevel === MMERiskLevel.High
                                                ? t('mme.calculator.highRiskWarning', 'Avertissement : Risque élevé')
                                                : t('mme.calculator.moderateRiskAlert', 'Alerte : Risque modéré'))}
                                        color={calculationResult.riskLevel === MMERiskLevel.High ? 'red' : 'orange'}
                                        mb="md"
                                    >
                                        <Text size="sm">
                                            {calculationResult.riskMessage}
                                        </Text>
                                    </Alert>
                                )}

                                <Stack gap="xs">
                                    {calculationResult.calculations.map((calc, index) => (
                                        <Box key={index} p="xs" style={{ backgroundColor: calc.hasWarning ? '#fff3cd' : '#f8f9fa' }}>
                                            <Group justify="space-between">
                                                <Text fw={500}>{calc.drugName}</Text>
                                                <Badge color={calc.hasWarning ? 'yellow' : 'green'} variant="light">
                                                    {calc.dailyMME.toFixed(2)} {t('mme.calculator.dailyMME')}
                                                </Badge>
                                            </Group>
                                            <Text size="xs" c="dimmed">
                                                {calc.dose} × {calc.frequencyPerDay}x/day × {calc.conversionFactor.toFixed(4)} = {calc.dailyMME.toFixed(2)} MME
                                            </Text>
                                            {calc.hasWarning && calc.warningMessage && (
                                                <Alert icon={<IconAlertTriangle size={14} />} color="yellow" mt="xs" p="xs">
                                                    <Text size="xs">{calc.warningMessage}</Text>
                                                </Alert>
                                            )}
                                        </Box>
                                    ))}
                                </Stack>

                            </Paper>
                        </Box>
                    )}
                </Stack>
            </Paper>
        );
    }),
);

const WrappedMMECalculator: React.FC = () => {
    return (
        <TableContextProvider>
            <MMECalculatorTable />
        </TableContextProvider>
    );
};

export default WrappedMMECalculator;
